import type MarketingClient from '@travelata-libs/tracker';
import { getCustomerProfile } from '@/shared/api/getCustomerProfile';
import { createMarketingClient } from '@/shared/api/marketingClient';
import { getTrackingMeta } from './trackingMeta';
import { TrackingEvent, type IntelligentSearchTracking, type TrackingData, type TrackingMeta } from '../model/types';

type BuildTrackingData = (meta: TrackingMeta) => TrackingData;

export default class Tracker {
  private marketingClientPromise: Promise<MarketingClient | null> | null = null;

  track(event: TrackingEvent, timestamp: number, buildData: BuildTrackingData): Promise<boolean> {
    if (typeof window === 'undefined') return Promise.resolve(false);

    return this.send(event, timestamp, buildData).catch(() => false);
  }

  trackIntelligentSearch({ inputValue, selectedData }: IntelligentSearchTracking): Promise<boolean> {
    return this.track(TrackingEvent.IntelligentSearch, Date.now(), (meta) => ({
      type: selectedData.type,
      id: selectedData.id,
      term: inputValue,
      ...meta,
      ab_identity: null,
      ab_identity_value: null,
    }));
  }

  private async send(event: TrackingEvent, timestamp: number, buildData: BuildTrackingData): Promise<boolean> {
    const marketingClient = await this.getMarketingClient();

    if (!marketingClient) return false;

    const customer = await getCustomerProfile();
    const data = buildData(getTrackingMeta({ timestamp, customerHash: customer?.hash ?? null }));

    const previousCustomerUuid = marketingClient.getCustomerUuid() ?? '';

    marketingClient.setCustomerUuid(customer?.uuid ?? '');

    try {
      marketingClient.track({ type: event, data });
    } finally {
      marketingClient.setCustomerUuid(previousCustomerUuid);
    }

    return true;
  }

  private async getMarketingClient(): Promise<MarketingClient | null> {
    this.marketingClientPromise ??= createMarketingClient();

    const marketingClientPromise = this.marketingClientPromise;
    const marketingClient = await marketingClientPromise;

    if (!marketingClient && this.marketingClientPromise === marketingClientPromise) {
      this.marketingClientPromise = null;
    }

    return marketingClient;
  }
}

const tracker = new Tracker();

export { tracker };
